'use client';

import { faAngleDown, faAngleUp } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import * as SelectPrimitive from '@radix-ui/react-select';
import { useState } from 'react';

import { Icon } from '@/components/ui/icon.component';
import { cn } from '@/utils/cn-tw-merge';

export interface SelectMenuOption {
  value: string;
  label: string;
}

interface SelectMenuProps {
  value: string;
  options: SelectMenuOption[];
  onValueChange: (value: string) => void;
  ariaLabel: string;
  disabled?: boolean;
  className?: string;
  dataTestId?: string;
}

export function SelectMenu({
  value,
  options,
  onValueChange,
  ariaLabel,
  disabled,
  className,
  dataTestId,
}: SelectMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <SelectPrimitive.Root
      value={value}
      onValueChange={onValueChange}
      open={isOpen}
      onOpenChange={setIsOpen}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger
        aria-label={ariaLabel}
        data-testid={dataTestId ?? DataTestIdEnum.SELECT_MENU_TRIGGER}
        className={cn(
          `flex w-full items-center gap-3 rounded-lg border bg-white px-4 py-3
           text-base leading-6 text-gray-500 outline-none
           data-[state=closed]:border-gray-300 data-[state=open]:border-gray-500
           disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400`,
          className,
        )}
      >
        <span className="flex-1 text-left">
          <SelectPrimitive.Value />
        </span>
        <SelectPrimitive.Icon asChild>
          <Icon icon={isOpen ? faAngleUp : faAngleDown} className="h-5 w-5 shrink-0 text-gray-500" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={0}
          className="z-50 w-[var(--radix-select-trigger-width)] overflow-hidden rounded-lg border border-gray-300 bg-white"
        >
          <SelectPrimitive.Viewport className="max-h-[var(--radix-select-content-available-height)]">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                data-testid={`${DataTestIdEnum.SELECT_MENU_OPTION}-${option.value}`}
                className={`flex cursor-pointer items-center border-b border-gray-300 px-4 py-3 text-base
                  leading-6 text-gray-700 outline-none last:border-b-0 data-[highlighted]:bg-gray-100`}
              >
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
